import { useTranslation } from "react-i18next";

import type { RichContent } from "@/app/api/types/chat-sessions";
import { GuideChip } from "@/components/guide/GuideChip";
import { DataTable } from "./DataTable";
import { BarChartRenderer } from "./BarChartRenderer";
import { LineChartRenderer } from "./LineChartRenderer";
import { PieChartRenderer } from "./PieChartRenderer";
import { ExpandableChart } from "./ExpandableChart";

interface RichContentRendererProps {
  readonly content: RichContent;
}

export function RichContentRenderer({ content }: RichContentRendererProps) {
  const { t } = useTranslation();

  switch (content.type) {
    case "table":
      return (
        <DataTable
          table={content.table}
          config={content.config}
          title={content.title}
        />
      );
    case "bar_chart":
      return (
        <ExpandableChart
          title={content.title}
          renderChart={({ height, showTitle }) => (
            <BarChartRenderer
              table={content.table}
              config={content.config}
              title={showTitle ? content.title : undefined}
              height={height}
            />
          )}
        />
      );
    case "line_chart":
      return (
        <ExpandableChart
          title={content.title}
          renderChart={({ height, showTitle }) => (
            <LineChartRenderer
              table={content.table}
              config={content.config}
              title={showTitle ? content.title : undefined}
              height={height}
            />
          )}
        />
      );
    case "pie_chart":
      return (
        <ExpandableChart
          title={content.title}
          renderChart={({ height, showTitle }) => (
            <PieChartRenderer
              table={content.table}
              config={content.config}
              title={showTitle ? content.title : undefined}
              height={height}
            />
          )}
        />
      );
    case "ui_guide":
      // The guide auto-starts from the live log stream; inline we render the
      // persistent trace + replay affordance.
      return <GuideChip content={content} />;
    default:
      return (
        <div className="text-muted-foreground text-sm">
          {t("charts.unknownChartType", "Unknown chart type")}
        </div>
      );
  }
}
